import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Callout,
  Label,
  LogoutIcon,
  SaveIcon,
  TextInput,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { t } from '../../i18n';
import { ApiError, api } from '../../services/api';

export function CompanySetupPage() {
  const { logout, patchUser } = useAuth();
  const navigate = useNavigate();
  const labels = t().tenantSetup;
  const [name, setName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(labels.requiredName);
      return;
    }

    setSaving(true);
    try {
      const tenant = await api.completeTenantSetup({
        name: name.trim(),
        vatNumber: vatNumber.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      patchUser({
        tenantName: tenant.name,
        tenantSetupCompleted: true,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4 text-ink">
      <div className="w-full max-w-lg rounded-xl border border-line bg-panel p-6 shadow-bf-sm sm:p-8">
        <h1 className="font-display text-2xl font-semibold">{labels.title}</h1>
        <p className="mt-2 text-sm text-muted">{labels.hint}</p>

        {error ? (
          <Callout variant="danger" className="mt-4">
            {error}
          </Callout>
        ) : null}

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div>
            <Label htmlFor="company-name">{labels.companyName}</Label>
            <TextInput
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="company-vat">{labels.vatNumber}</Label>
            <TextInput
              id="company-vat"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="company-email">{labels.email}</Label>
            <TextInput
              id="company-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="company-phone">{labels.phone}</Label>
            <TextInput
              id="company-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="company-address">{labels.address}</Label>
            <TextInput
              id="company-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="company-notes">{labels.notes}</Label>
            <TextInput
              id="company-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="submit" disabled={saving}>
              <SaveIcon className="h-4 w-4" />
              {saving ? t().common.loading : labels.submit}
            </Button>
            <Button type="button" variant="ghost" onClick={logout}>
              <LogoutIcon className="h-4 w-4" />
              {t().nav.logout}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
